import { Button, Empty, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined } from "@ant-design/icons";
import React from "react";
import { Link } from "react-router-dom";
import { ShopRequest } from "../../interfaces/shop-request.interface";
import "./shop-request-table.scss";

interface ShopRequestTableProps {
  shopRequests: ShopRequest[];
  loading?: boolean;
}

const ShopRequestTable: React.FC<ShopRequestTableProps> = ({
  shopRequests,
  loading = false,
}) => {
  const columns: ColumnsType<ShopRequest> = [
    {
      title: "Owner Name",
      dataIndex: "ownerName",
      key: "ownerName",
      ellipsis: true,
      width: 180,
      sorter: (a, b) => (a.ownerName || "").localeCompare(b.ownerName || ""),
      render: (value: string) => (
        <Typography.Text strong>{value || "-"}</Typography.Text>
      ),
    },
    {
      title: "Shop Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      width: 180,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (value: string) => value || "-",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      width: 240,
      sorter: (a, b) => (a.address || "").localeCompare(b.address || ""),
      render: (value: string) => (
        <Typography.Text type="secondary">{value || "-"}</Typography.Text>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      width: 220,
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
      render: (value: string) =>
        value ? (
          <Typography.Link href={`mailto:${value}`}>{value}</Typography.Link>
        ) : (
          "-"
        ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      ellipsis: true,
      width: 160,
      sorter: (a, b) => (a.phone || "").localeCompare(b.phone || ""),
      render: (value: string) =>
        value ? (
          <Typography.Link href={`tel:${value}`}>{value}</Typography.Link>
        ) : (
          "-"
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      sorter: (a, b) =>
        (a.description || "").localeCompare(b.description || ""),
      render: (value: string) => (
        <Typography.Text type="secondary">{value || "-"}</Typography.Text>
      ),
    },
    {
      title: "Source",
      dataIndex: "userId",
      key: "userId",
      width: 180,
      sorter: (a, b) => (a.userId || "").localeCompare(b.userId || ""),
      render: (userId: string) =>
        userId === "whappo-website" ? (
          <Tag
            color="blue"
            style={{
              borderRadius: 999,
              paddingInline: 10,
              fontWeight: 600,
            }}
          >
            From Website
          </Tag>
        ) : (
          <Tag
            style={{
              borderRadius: 999,
              paddingInline: 10,
              fontWeight: 600,
            }}
          >
            {userId || "Unknown"}
          </Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 160,
      render: (_, record) => (
        <Link to={`/shops/new?requestId=${record._id}`}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{
              borderRadius: 12,
              fontWeight: 600,
              boxShadow: "0 8px 20px rgba(22, 119, 255, 0.18)",
            }}
          >
            Add Shop
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <Table
      rowKey="_id"
      loading={loading}
      dataSource={shopRequests}
      columns={columns}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
      }}
      scroll={{ x: 1400 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No shop request found"
          />
        ),
      }}
      style={{
        background: "#fff",
        borderRadius: 20,
        overflow: "hidden",
      }}
      rowClassName={(_, index) =>
        index % 2 === 0 ? "shop-request-row-even" : "shop-request-row-odd"
      }
    />
  );
};

export default ShopRequestTable;
